import React, { useEffect, useRef, useState } from "react";
import $ from "jquery";
import { gsap } from "gsap";
import ApiURl from "../../controllers/Api";

function AdminHeroSettings() {
  const [settings, setSettings] = useState({
    kicker: "discover",
    title: "Ecommerce Products",
    description: "Explore categories and featured products. Shop home & kitchen essentials with a clean and modern experience.",
    button_text: "Explore Now",
    button_link: "#home-products"
  });
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, type: "info", msg: "" });

  const pageRef = useRef(null);

  const showToast = (type, msg) => {
    setToast({ show: true, type, msg });
    gsap.fromTo(
      ".toast-anim",
      { y: 12, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.25, ease: "power2.out" }
    );
    clearTimeout(showToast._t);
    showToast._t = setTimeout(() => setToast((t) => ({ ...t, show: false })), 2600);
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await $.getJSON(`${ApiURl}/getHeroSettings.php`);
      if (response?.success) {
        setSettings(response.settings);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
      showToast("error", "Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const formData = new FormData();
    formData.append("kicker", settings.kicker);
    formData.append("title", settings.title);
    formData.append("description", settings.description);
    formData.append("button_text", settings.button_text);
    formData.append("button_link", settings.button_link);

    try {
      const response = await $.ajax({
        url: `${ApiURl}/updateHeroSettings.php`,
        type: "POST",
        data: formData,
        processData: false,
        contentType: false,
        dataType: "json"
      });

      if (response?.success) {
        showToast("success", "Settings updated successfully");
      } else {
        showToast("error", response?.message || "Failed to update settings");
      }
    } catch (error) {
      console.error("Error updating settings:", error);
      showToast("error", "Server error while updating settings");
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setSettings({
      ...settings,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div ref={pageRef} className="w-full font-['Poppins',sans-serif] bg-gray-50 min-h-screen">
      {toast.show && (
        <div className="toast-anim fixed bottom-5 right-5 z-[9999]">
          <div
            className={[
              "rounded-xl px-4 py-3 shadow-lg border text-sm",
              toast.type === "success"
                ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                : toast.type === "error"
                ? "bg-rose-50 border-rose-200 text-rose-900"
                : "bg-slate-50 border-slate-200 text-slate-900",
            ].join(" ")}
          >
            {toast.msg}
          </div>
        </div>
      )}

      <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">
            Hero Section Settings
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Customize the text that appears in the hero section of your homepage.
          </p>
        </div>

        {loading ? (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
            <p className="text-slate-600">Loading settings...</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
            <div className="px-6 py-5 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
              <h2 className="text-lg font-semibold text-slate-900">Hero Text Settings</h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-6">
                {/* Kicker Text */}
                <div>
                  <label className="block mb-1 text-sm font-medium text-slate-900">
                    Kicker Text <span className="text-slate-400">(small uppercase text)</span>
                  </label>
                  <input
                    type="text"
                    name="kicker"
                    value={settings.kicker}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#b29063]/30 focus:border-[#b29063]"
                    placeholder="e.g., discover"
                  />
                </div>

                {/* Main Title */}
                <div>
                  <label className="block mb-1 text-sm font-medium text-slate-900">
                    Main Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={settings.title}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#b29063]/30 focus:border-[#b29063]"
                    placeholder="e.g., Ecommerce Products"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block mb-1 text-sm font-medium text-slate-900">
                    Description
                  </label>
                  <textarea
                    name="description"
                    rows={4}
                    value={settings.description}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#b29063]/30 focus:border-[#b29063]"
                    placeholder="Enter description..."
                  />
                </div>

                {/* Button Text & Link */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block mb-1 text-sm font-medium text-slate-900">
                      Button Text
                    </label>
                    <input
                      type="text"
                      name="button_text"
                      value={settings.button_text}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#b29063]/30 focus:border-[#b29063]"
                      placeholder="Explore Now"
                    />
                  </div>
                  <div>
                    <label className="block mb-1 text-sm font-medium text-slate-900">
                      Button Link
                    </label>
                    <input
                      type="text"
                      name="button_link"
                      value={settings.button_link}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#b29063]/30 focus:border-[#b29063]"
                      placeholder="#home-products"
                    />
                  </div>
                </div>

                {/* Preview Card */}
                <div className="mt-8 p-6 bg-slate-50 rounded-xl border border-slate-200">
                  <h3 className="text-sm font-medium text-slate-900 mb-3">Preview</h3>
                  <div className="bg-white p-6 rounded-lg border border-slate-200">
                    <span className="text-gray-600 uppercase tracking-[1.5px] text-sm block mb-2">
                      {settings.kicker}
                    </span>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      {settings.title}
                    </h2>
                    <p className="text-gray-700 text-sm mb-4">
                      {settings.description}
                    </p>
                    <button className="inline-flex items-center px-4 py-2 bg-[#b29063] text-white text-sm rounded-xl hover:bg-[#a08053]">
                      {settings.button_text} →
                    </button>
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <button
                    type="submit"
                    disabled={submitting}
                    className={[
                      "inline-flex items-center justify-center rounded-xl px-6 py-3 text-sm font-semibold text-white transition",
                      submitting ? "bg-slate-400 cursor-not-allowed" : "bg-[#b29063] hover:bg-[#a08053] active:scale-[0.99]",
                    ].join(" ")}
                  >
                    {submitting ? "Saving..." : "Save Settings"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminHeroSettings;